import { Pipe, PipeTransform } from '@angular/core';

/**
 * PhoneNumberPipe
 *
 * A custom Angular pipe that formats a numeric phone number string into a
 * standard US phone number format: +1 (XXX)XXX-XXXX.
 *
 *
 * ## Example:
 *
 *   {{ '4083828232' | phoneNumber }}
 *   // Output: +1 (408)382-8232
 *
 * ## Example2:
 *
 *   {{ '4083828232' | phoneNumber : '(XXX) XXX-XXXX' }}
 *   // Output: (408) 382-8232
 *
 *
 * @param value {string} The raw phone number input as a string.
 * @param template {string} The phone number template string.
 * @returns {string} The formatted phone number or the original value if invalid.
 */

@Pipe({
  name: 'phone',
  standalone: true,
})
export class PhonePipe implements PipeTransform {
  private readonly defaultFormat = '(XXX) XXX-XXXX';

  transform(value: unknown, template?: string): string {
    if (this.isPrefixedUsPhoneNumber(value)) {
      const formatTemplate = template ?? this.defaultFormat;
      return this.getFormattedPhoneNumber(value.slice(2), formatTemplate, '+1');
    }

    if (!this.isRawUsPhoneNumber(value)) {
      return value as string;
    }

    if (template === undefined) {
      return this.getFormattedPhoneNumber(value, this.defaultFormat, '+1');
    }

    return this.getFormattedPhoneNumber(value, template);
  }

  private isPrefixedUsPhoneNumber(value: unknown): value is string {
    return typeof value === 'string' && value.length === 12 && value.startsWith('+1');
  }

  private isRawUsPhoneNumber(value: unknown): value is string {
    return typeof value === 'string' && value.length === 10;
  }

  private getFormattedPhoneNumber(phoneNumber: string, template: string, prefix?: string): string {
    let formattedNumber = '';
    let digitIndex = 0;

    for (const char of template) {
      if ((char === 'X' || char === '0') && digitIndex < phoneNumber.length) {
        formattedNumber += phoneNumber[digitIndex];
        digitIndex++;
      } else {
        formattedNumber += char;
      }
    }

    if (prefix) {
      return prefix + ' ' + formattedNumber;
    }

    return formattedNumber;
  }
}
